import { Plus, Check } from "lucide-react";
import { useState } from "react";
import { MENU_GENERAL_SECTIONS } from "../data/menu";
import { useCart } from "../context/CartContext";

const formatPrice = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(n);

// ── Row for a simple product (single price) ──────────────────────────
const SimpleRow = ({ item }) => {
  const { addItem } = useCart();
  const [justAdded, setJustAdded] = useState(false);

  const handleAdd = () => {
    addItem(item);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1200);
  };

  return (
    <li
      data-testid={`gm-item-${item.id}`}
      className="flex items-start gap-4 py-4 border-b border-[#E6DFD3] last:border-b-0"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="font-serif text-lg sm:text-xl text-[#2B241D] leading-tight">
            {item.name}
          </h4>
          <span
            data-testid={`gm-price-${item.id}`}
            className="font-serif text-lg sm:text-xl text-[#D96C42] whitespace-nowrap"
          >
            {formatPrice(item.price)}
          </span>
        </div>
        {item.description && (
          <p className="font-sans text-sm text-[#5C5146] font-light leading-relaxed mt-1.5">
            {item.description}
          </p>
        )}
      </div>
      <button
        data-testid={`gm-add-${item.id}`}
        onClick={handleAdd}
        aria-label={`Añadir ${item.name} al carrito`}
        className={`shrink-0 w-11 h-11 rounded-full inline-flex items-center justify-center transition-all duration-200 ${
          justAdded
            ? "bg-[#25D366] text-white"
            : "bg-[#2B241D] text-[#F5EBD3] hover:bg-[#D96C42]"
        }`}
      >
        {justAdded ? (
          <Check size={18} strokeWidth={2} />
        ) : (
          <Plus size={18} strokeWidth={2} />
        )}
      </button>
    </li>
  );
};

// ── Row for a pizza (with 6 size options) ────────────────────────────
const PizzaRow = ({ item }) => {
  const { addItem } = useCart();
  const [selectedSize, setSelectedSize] = useState(
    item.sizes[Math.min(2, item.sizes.length - 1)]
  );
  const [justAdded, setJustAdded] = useState(false);

  const handleAdd = () => {
    addItem(item, { size: selectedSize.name, price: selectedSize.price });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1200);
  };

  return (
    <li
      data-testid={`gm-item-${item.id}`}
      className="py-4 border-b border-[#E6DFD3] last:border-b-0"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="font-serif text-lg sm:text-xl text-[#2B241D] leading-tight">
          {item.name}
        </h4>
        <span
          data-testid={`gm-price-${item.id}`}
          className="font-serif text-lg sm:text-xl text-[#D96C42] whitespace-nowrap"
        >
          {formatPrice(selectedSize.price)}
        </span>
      </div>
      {item.description && (
        <p className="font-sans text-sm text-[#5C5146] font-light leading-relaxed mt-1.5">
          {item.description}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {item.sizes.map((s) => {
          const active = selectedSize.name === s.name;
          return (
            <button
              key={s.name}
              data-testid={`gm-size-${item.id}-${s.name.toLowerCase()}`}
              onClick={() => setSelectedSize(s)}
              className={`text-[11px] font-medium px-3 py-1.5 rounded-full border transition-all ${
                active
                  ? "bg-[#2B241D] text-[#F5EBD3] border-[#2B241D]"
                  : "bg-[#F4EFE6] text-[#2B241D] border-[#E6DFD3] hover:border-[#D96C42]"
              }`}
            >
              {s.name}
            </button>
          );
        })}
        <button
          data-testid={`gm-add-${item.id}`}
          onClick={handleAdd}
          className={`ml-auto inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-all duration-200 ${
            justAdded
              ? "bg-[#25D366] text-white"
              : "bg-[#2B241D] text-[#F5EBD3] hover:bg-[#D96C42]"
          }`}
        >
          {justAdded ? (
            <>
              <Check size={14} strokeWidth={2} />
              Añadido
            </>
          ) : (
            <>
              <Plus size={14} strokeWidth={2} />
              Añadir {selectedSize.name}
            </>
          )}
        </button>
      </div>
    </li>
  );
};

// ── Section (marketing image + list of items) ────────────────────────
const Section = ({ section }) => {
  return (
    <div
      data-testid={`gm-section-${section.id}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 sm:mb-24 last:mb-0"
    >
      {/* Marketing image */}
      {!section.hideImage && (
        <div className="lg:col-span-5">
          <div className="sticky top-28 rounded-2xl overflow-hidden shadow-xl shadow-[#2B241D]/10 border border-[#E6DFD3]">
            <img
              src={section.image}
              alt={`Menú ${section.title}`}
              className="w-full h-auto object-cover"
              data-testid={`gm-image-${section.id}`}
            />
          </div>
        </div>
      )}

      {/* Product list */}
      <div className={section.hideImage ? "lg:col-span-12" : "lg:col-span-7"}>
        <div className="mb-6">
          <div className="font-sans text-xs uppercase tracking-[0.3em] text-[#D96C42] mb-3">
            — {section.title}
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#2B241D] leading-tight">
            {section.subtitle}
          </h3>
        </div>

        <ul className="bg-[#F5EBD3] rounded-2xl border border-[#E6DFD3] px-5 sm:px-6 py-2">
          {section.items.map((item) =>
            item.sizes ? (
              <PizzaRow key={item.id} item={item} />
            ) : (
              <SimpleRow key={item.id} item={item} />
            )
          )}
        </ul>

        {section.footNote && (
          <p className="font-sans text-xs text-[#5C5146] mt-4 leading-relaxed">
            {section.footNote}
          </p>
        )}
      </div>
    </div>
  );
};

export const MenuGeneral = () => {
  const { openDrawer, totalItems } = useCart();

  return (
    <section
      id="menu-general"
      data-testid="menu-general-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#F4EFE6]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 sm:mb-24">
          <div className="md:col-span-7">
            <div className="font-sans text-xs uppercase tracking-[0.3em] text-[#D96C42] mb-6">
              — Menú completo
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium text-[#2B241D]">
              Todo el menú, <em className="italic font-normal">a un toque.</em>
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="font-sans text-base sm:text-lg text-[#2B241D]/75 leading-relaxed font-light">
              Pizzas, hamburguesas, snacks y más. Añade lo que quieras al
              carrito y envía tu pedido por WhatsApp.
            </p>
          </div>
        </div>

        {/* Sections */}
        <div>
          {MENU_GENERAL_SECTIONS.map((section) => (
            <Section key={section.id} section={section} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 sm:mt-12 text-center">
          <button
            data-testid="menu-general-view-cart"
            onClick={openDrawer}
            className="inline-flex items-center gap-3 bg-[#2B241D] text-[#F5EBD3] px-8 py-4 rounded-full text-sm font-medium hover:bg-[#D96C42] transition-colors"
          >
            Ver mi carrito
            {totalItems > 0 && (
              <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-[#D96C42] text-[#F5EBD3] text-xs font-medium">
                {totalItems}
              </span>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};
